<template>
  <div class="regional-list">
    <div class="regional-list__header">
      <div>
        <s-search-input
          v-model="searchTerm"
          v-s-trigger="changingSearch"
          :placeholder="$t('regional-manager.query.search-placeholder')"
        />
      </div>
      <div>
        <b-button
          variant="primary"
          @click="newRegional"
        >
          {{ $t('regional-manager.query.new-regional') }}
        </b-button>
      </div>
    </div>
    <s-table
      class="regional-list__table"
      :busy="loading"
      :fields="fields"
      :items="items"
      :sort-by="sortBy.field"
      :sort-desc="sortBy.desc"
      @sort-changed="sort"
    >
      <template #cell(installations)="data">
        <list-aggregator
          modal-item-name-key="installationName"
          suppressedLabelPath="user-manager.query.list.suppressed-installations"
          :modal-title="data.item.name"
          :modal-table-items="data.item.installations"
          :modal-table-header="$t('entities.installations')"
        />
      </template>

      <template #cell(actions)="data">
        <b-button
          variant="outline-primary"
          size="sm"
          @click="editRegional(data.item)"
        >
          <span class="fa fa-edit" />
          {{ $t('user-manager.query.list.edit') }}
        </b-button>
        <b-button
          variant="primary"
          size="sm"
          @click="deleteRegional(data.item)"
        >
          <span class="far fa-trash-alt" />
          {{ $t('user-manager.query.list.delete') }}
        </b-button>
      </template>
    </s-table>
    <grid-paginator
      v-model="currentPage"
      :total-rows="totalItems"
      :per-page="perPage"
      @input="paging()"
    />
  </div>
</template>

<script>
import GridPaginator from '@/components/lists/Paginator';
import ListAggregator from '@/components/listAggregator';
import { delayed } from '@/services/observables';
import Service from '@/domain/services/generalPanel/adminPanel/regional/regionalService';

export default {
  components: {
    GridPaginator,
    ListAggregator,
  },
  data: vm => ({
    searchTerm: '',
    doDelayedRequest: delayed((...args) => vm.doRequest(...args)),
    currentPage: 1,
    totalItems: null,
    perPage: 10,
    items: [],
    loading: true,
    sortBy: {
      field: 'name',
      desc: false,
    },
  }),
  methods: {
    sort(evt) {
      this.sortBy.field = evt.sortBy;
      this.sortBy.desc = evt.sortDesc;
      this.currentPage = 1;
      this.refreshItems();
      return false;
    },
    paging() {
      this.refreshItems();
    },
    changingSearch() {
      if (this.searchTerm && this.searchTerm.length < 3) return;
      this.currentPage = 1;
      this.refreshItems();
    },
    async refreshItems() {
      this.loading = true;
      this.items = [];
      try {
        return await this.doDelayedRequest(this.getRequestFilter());
      } finally {
        this.loading = false;
      }
    },
    doRequest(requestFilter) {
      return Service
        .getRegionalList(requestFilter)
        .then(this.getProjectRequestResult());
    },
    getRequestFilter() {
      const sort = {};
      sort[this.sortBy.field] = this.sortBy.desc ? 'desc' : 'asc';
      return {
        filter: {
          name: this.searchTerm,
        },
        page: this.currentPage,
        pageSize: this.perPage,
        sort,
      };
    },
    getProjectRequestResult() {
      return (paged) => {
        this.totalItems = paged.total;
        this.items = paged.data;
      };
    },
    editRegional(regional) {
      this.$router.push({ name: 'regional.edit', params: { regionalId: regional.id } });
    },
    async deleteRegional(regional) {
      if (!(await this.$confirmMessage({ code: 'MC023' }).toBool())) {
        return;
      }
      await Service.removeRegional(regional.id);
      this.$pushAlert({ code: 'MS007' });
      await this.refreshItems();
    },
    newRegional() {
      this.$router.push({ name: 'regional.new' });
    },
  },
  computed: {
    fields() {
      const prefix = 'regional-manager.query.list.column-titles';
      return [
        {
          key: 'name',
          sortable: true,
          label: this.$t(`${prefix}.regional`),
          class: 'regional-list__cell-name',
        },
        {
          key: 'companyName',
          sortable: true,
          label: this.$t(`${prefix}.company`),
          class: 'regional-list__cell-company-name',
        },
        {
          key: 'installations',
          sortable: true,
          label: this.$t(`${prefix}.installation`),
          class: 'regional-list__cell-installations',
        },
        {
          key: 'description',
          label: this.$t(`${prefix}.description`),
          class: 'truncate regional-list__cell-description',
        },
        {
          key: 'actions',
          label: '',
          class: 'regional-list__cell-actions',
        },
      ];
    },
  },
  created() {
    this.refreshItems();
  },
};
</script>

<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .regional-list
    &__header
      display: flex
      flex-direction: column
      padding: 10px
      button
        margin-top: 1em
    &__search-input
      width: 350px
    +media-min-sm()
      &__header
        flex-direction: row
        justify-content: space-between
        button
          margin-top: 0
          margin-left: 1em
    ::v-deep &__table
      th,
      td
        &:first-child
          text-align: left
        &:not(:first-child):not(:last-child)
          text-align: center
        &:last-child
          text-align: right
    ::v-deep &__cell
      &-name,
      &-company-name,
      &-installations
        min-width: 100px
        width: 20%
      &-description
        max-width: 200px
      &-actions
        min-width: 200px
        width: 20%
</style>
